import { IconCalendar16 } from '@warp-ds/icons/react';
import type { TextField } from '@warp-ds/react';
import cn from 'classnames';
import { cloneElement, type FocusEvent, type KeyboardEvent, type ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import { useBreakpoints } from '../../hooks/useBreakpoints';
import useClickOutside from '../../hooks/useClickOutside';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { safeFormatClientDate, safeParseClientDate } from '../../util/datetime-fns';
import { CloseCalendarButton } from '../calendar/CloseCalendarButton';
import { Direction, NavBtn } from '../calendar/NavBtn';
import styles from './BasicDatePicker.module.css';
import { MonthPanel } from './components/MonthPanel';
import { useDateNavigation } from './hooks/useDateNavigation';
import useDatePickerChildValidation from './hooks/useDatePickerChildValidation';
import { useInitializeDateRange } from './hooks/useInitializeDateRange';
import useKeyboardNavigation from './hooks/useKeyboardNavigation';
import useLockBodyScroll from './hooks/useLockBodyScroll';
import { useSyncDisplayedDate } from './hooks/useSyncDisplayedDate';
import { useUpdateDateRange } from './hooks/useUpdateDateRange';
import { useUpdateSingleDate } from './hooks/useUpdateSingleDate';
import type { SharedDatePickerProps } from './types/DatePickerProps';
import { handleAutoFormatOnBlur, handleDateInputChange, handleDateInputKeyDown } from './utils/inputHandlers';

export interface SingleSelectionProps extends SharedDatePickerProps {
    onDateChange?: (dates: { startDate: Date | null }) => void;
    useRange: false;
    children: ReactElement<typeof TextField>;
}

export interface RangeSelectionProps extends SharedDatePickerProps {
    onDateChange?: (dates: { startDate: Date | null; endDate: Date | null }) => void;
    useRange: true;
    children: [ReactElement<typeof TextField>, ReactElement<typeof TextField>];
}

export const BasicDatePicker = ({
    onDateChange,
    startDate: initialStartDate = null,
    endDate: initialEndDate = null,
    visibleMonths,
    useRange,
    hidePaddingDays = true,
    dayClassName,
    modalFooter,
    modalHeader,
    children,
    dateData,
    upperDateLimit,
    className,
    setDisplayedDate,
    disabledDateRanges,
    useAnchor,
}: SingleSelectionProps | RangeSelectionProps) => {
    const [startDate, setStartDate] = useState<Date | null>(initialStartDate);
    const [endDate, setEndDate] = useState<Date | null>(initialEndDate);
    const [inputStartDate, setInputStartDate] = useState<string>(safeFormatClientDate(initialStartDate));
    const [inputEndDate, setInputEndDate] = useState<string>(safeFormatClientDate(initialEndDate));
    const [endDateFirst, setEndDateFirst] = useState<boolean>(false);
    const [isCalendarVisible, setIsCalendarVisible] = useState<boolean>(false);
    const [focusedDate, setFocusedDate] = useState<Date>(new Date());
    const [displayDates, setDisplayDates] = useState<Date>(new Date());
    const [isStartDateFocused, setIsStartDateFocused] = useState<boolean>(false);
    const [isEndDateFocused, setIsEndDateFocused] = useState<boolean>(false);

    const isSmallScreen = useBreakpoints({ breakpoint: 640 });
    const isMediumScreen = useBreakpoints({ breakpoint: 'md' });

    const effectiveVisibleMonths = useMemo(() => (isSmallScreen ? 1 : visibleMonths), [isSmallScreen, visibleMonths]);

    const modalRef = useRef<HTMLDivElement | null>(null);

    const { handleNextPrevMonth, isPrevDisabled, isNextDisabled } = useDateNavigation({
        visibleMonths: effectiveVisibleMonths,
        displayDates,
        setDisplayDates,
        modalRef,
        upperDateLimit,
    });

    const showCalendar = (isEndDate: boolean) => {
        setIsCalendarVisible(true);
        setEndDateFirst(isEndDate && !startDate);

        const newDisplayDate = startDate
            ? new Date(startDate.getFullYear(), startDate.getMonth(), 1)
            : endDate
              ? new Date(endDate.getFullYear(), endDate.getMonth(), 1)
              : displayDates;

        setDisplayDates(newDisplayDate);
    };

    const hideCalendar = useCallback(() => {
        setIsCalendarVisible(false);
        setIsStartDateFocused(false);
        setIsEndDateFocused(false);
    }, []);

    useFocusTrap(isCalendarVisible, modalRef);
    useLockBodyScroll(isCalendarVisible && isSmallScreen);
    useDatePickerChildValidation(useRange, children);
    useClickOutside(modalRef, hideCalendar);
    useKeyboardNavigation(focusedDate, setFocusedDate, modalRef, hideCalendar, handleNextPrevMonth, effectiveVisibleMonths, displayDates);
    useSyncDisplayedDate(displayDates, setDisplayedDate);

    useInitializeDateRange({
        initialStartDate,
        initialEndDate,
        startDate,
        endDate,
        setStartDate,
        setEndDate,
        setInputStartDate,
        setInputEndDate,
        formatDate: safeFormatClientDate,
        useRange,
    });

    const updateDateRange = useRange
        ? // biome-ignore lint/correctness/useHookAtTopLevel: test
          useUpdateDateRange({
              startDate,
              endDate,
              endDateFirst,
              setStartDate: (date) => {
                  setStartDate(date);
                  setInputStartDate(safeFormatClientDate(date));
                  onDateChange?.({ startDate: date, endDate });
              },
              setEndDate: (date) => {
                  setEndDate(date);
                  setInputEndDate(safeFormatClientDate(date));
                  onDateChange?.({ startDate, endDate: date });
              },
              setInputStartDate,
              setInputEndDate,
              setEndDateFirst,
              setIsStartDateFocused,
              setIsEndDateFocused,
          })
        : // biome-ignore lint/correctness/useHookAtTopLevel: test
          useUpdateSingleDate({
              startDate,
              setStartDate: (date) => {
                  setStartDate(date);
                  setInputStartDate(safeFormatClientDate(date));
                  if (useRange) {
                      (onDateChange as RangeSelectionProps['onDateChange'])?.({ startDate: date, endDate });
                  } else {
                      (onDateChange as SingleSelectionProps['onDateChange'])?.({ startDate: date });
                  }
              },
              setInputStartDate,
              setIsStartDateFocused,
          });

    const commonProps = {
        readOnly: isCalendarVisible && isSmallScreen,
        ...(isSmallScreen ? { inputMode: 'none' } : {}),
        ...(isSmallScreen ? { onFocus: (e: FocusEvent<HTMLInputElement>) => e.target.blur() } : {}),
        placeholder: 'dd.mm.åååå',
        'aria-haspopup': 'grid',
        'aria-expanded': isCalendarVisible,
        role: 'combobox',
        type: 'text',
    };

    const dateProps: Pick<SingleSelectionProps, 'onDateChange' | 'useRange'> | Pick<RangeSelectionProps, 'onDateChange' | 'useRange'> =
        useRange ? { useRange: true, onDateChange } : { useRange: false, onDateChange };

    const generateInputProps = (isEndDate: boolean, value: string, focused: boolean) => ({
        ...commonProps,
        value,
        onClick: () => {
            showCalendar(isEndDate);
            setIsStartDateFocused(!isEndDate);
            setIsEndDateFocused(isEndDate);
        },
        onKeyDown: (e: KeyboardEvent<HTMLInputElement>) =>
            handleDateInputKeyDown(
                e,
                value,
                isEndDate,
                dateProps,
                setStartDate,
                setEndDate,
                showCalendar,
                setIsStartDateFocused,
                setIsEndDateFocused,
            ),
        onChange: (e: { target: { value: string } }) => {
            handleDateInputChange(
                e.target.value,
                isEndDate ? setInputEndDate : setInputStartDate,
                isEndDate ? setEndDate : setStartDate,
                safeParseClientDate,
                isEndDate,
                setEndDateFirst,
                setIsStartDateFocused,
                setIsEndDateFocused,
                startDate,
                endDate,
                dateProps,
            );
        },
        onBlur: (e: { target: { value: string } }) => {
            const handler = isEndDate ? setInputEndDate : setInputStartDate;
            const dateSetter = isEndDate ? setEndDate : setStartDate;
            handleAutoFormatOnBlur(e.target.value, handler, dateSetter, safeParseClientDate);
        },
        className: cn('date-picker__input', focused ? 'date-picker__visual-focus' : 'date-picker__no-visual-focus', {
            'date-picker__input--small-screen': isSmallScreen,
            'date-picker__input--medium-screen': isMediumScreen,
            'date-picker__input--start-date': !isEndDate,
            'date-picker__input--end-date': isEndDate,
        }),
    });

    const childArray = Array.isArray(children) ? children : [children];

    const childrenWithProps = childArray.map((child, index) => {
        const isEndDate = index === 1;
        const inputProps = isEndDate
            ? generateInputProps(true, inputEndDate, isEndDateFocused)
            : generateInputProps(false, inputStartDate, isStartDateFocused);

        return (
            <div key={child.key || `date-picker-child-${index}`} className="date-picker__input-wrapper">
                <div className="date-picker__input-prefix">
                    <IconCalendar16 className="date-picker__input-icon" aria-hidden />
                </div>
                {cloneElement(child, {
                    ...inputProps,
                    ...child.props,
                })}
            </div>
        );
    });

    const fieldsetId = useRef(`date-picker-fieldset-${Math.random().toString(36).slice(2, 9)}`);

    return (
        <>
            {isCalendarVisible && isSmallScreen && (
                <div className={styles['date-picker__overlay']} onPointerDown={hideCalendar} aria-hidden="true" />
            )}
            <div className={cn(styles['date-picker'], className)} ref={modalRef}>
                {useAnchor ? (
                    <>
                        <button
                            onClick={() => showCalendar(!!endDate)}
                            type="button"
                            className={cn('date-picker__button', {
                                'date-picker__button--error': useAnchor.invalid,
                            })}>
                            <IconCalendar16 className="date-picker__button__icon" aria-hidden />
                            <span className="date-picker__button__text">
                                {startDate
                                    ? `${safeFormatClientDate(startDate)} ${endDate ? ` - ${safeFormatClientDate(endDate)}` : ''}`
                                    : useAnchor.title}
                            </span>
                        </button>
                        {useAnchor.helpText && (
                            <span
                                className={cn('date-picker__help-text', {
                                    'date-picker__help-text--error': useAnchor.invalid,
                                })}>
                                {useAnchor.helpText}
                            </span>
                        )}
                    </>
                ) : (
                    <fieldset
                        id={fieldsetId.current}
                        className={cn('date-picker__fieldset', {
                            'date-picker__fieldset--small-screen': isSmallScreen,
                            'date-picker__fieldset--medium-screen': isMediumScreen,
                            'date-picker__fieldset--single-date': !useRange,
                        })}>
                        <legend className="sr-only">{useRange ? 'Velg datoer' : 'Velg dato'}</legend>
                        {childrenWithProps}
                    </fieldset>
                )}
                {isCalendarVisible && (
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={fieldsetId.current}
                        onPointerDown={(e) => e.stopPropagation()}
                        className={cn('date-picker__modal', {
                            'date-picker__modal--small-screen': isSmallScreen,
                            'date-picker__modal--medium-screen': isMediumScreen,
                            'date-picker__modal--single-month': !isSmallScreen && effectiveVisibleMonths === 1,
                            'date-picker__modal--open': isCalendarVisible,
                            'date-picker__modal--closed': !isCalendarVisible,
                        })}>
                        {modalHeader}
                        <nav
                            className={cn('date-picker__modal-header', {
                                'date-picker__modal-header--small-screen': isSmallScreen,
                                'date-picker__modal-header--medium-screen': isMediumScreen,
                            })}>
                            <NavBtn direction={Direction.Left} onClick={() => handleNextPrevMonth('prev')} disabled={isPrevDisabled} />
                            <NavBtn direction={Direction.Right} onClick={() => handleNextPrevMonth('next')} disabled={isNextDisabled} />
                        </nav>
                        <div
                            className={cn('date-picker__month-panel-group', {
                                'date-picker__month-panel-group--small-screen': isSmallScreen,
                            })}>
                            {[...Array(effectiveVisibleMonths)].map((_, index) => {
                                const month = (displayDates.getMonth() + index) % 12;
                                const year =
                                    displayDates.getMonth() + index >= 12 ? displayDates.getFullYear() + 1 : displayDates.getFullYear();
                                return (
                                    <MonthPanel
                                        key={`month-panel-${index + 1}`}
                                        year={year}
                                        month={month}
                                        startDate={startDate}
                                        endDate={endDate}
                                        updateDateRange={updateDateRange}
                                        hidePaddingDays={hidePaddingDays}
                                        focusedDate={focusedDate}
                                        dateData={dateData}
                                        dayClassName={dayClassName}
                                        upperDateLimit={upperDateLimit}
                                        disabledDateRanges={disabledDateRanges}
                                    />
                                );
                            })}
                        </div>

                        <div
                            className={cn('date-picker__modal-footer', {
                                'date-picker__modal-footer--small-screen': isSmallScreen,
                                'date-picker__modal-footer--medium-screen': isMediumScreen,
                            })}>
                            <div className="date-picker__modal-footer__optional">{modalFooter}</div>
                            {useAnchor?.helpText && (
                                <span
                                    className={cn('date-picker__help-text', {
                                        'date-picker__help-text--error': useAnchor.invalid,
                                    })}>
                                    {useAnchor.helpText}
                                </span>
                            )}
                            <CloseCalendarButton
                                className="date-picker__modal-footer__button"
                                onClick={hideCalendar}
                                setOpen={setIsCalendarVisible}
                                text="Bruk"
                            />
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};
